import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { toast } from "sonner";
import {
  Database, Trash2, RotateCcw, ShieldAlert, Activity, Download,
  Plus, FileClock, Search, X,
} from "lucide-react";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-800 border-emerald-200",
  update: "bg-blue-100 text-blue-800 border-blue-200",
  delete: "bg-amber-100 text-amber-800 border-amber-200",
  restore: "bg-slate-100 text-slate-800 border-slate-200",
  purge: "bg-rose-100 text-rose-800 border-rose-200",
  approve: "bg-emerald-100 text-emerald-800 border-emerald-200",
  reject: "bg-rose-100 text-rose-800 border-rose-200",
  download: "bg-slate-100 text-slate-800 border-slate-200",
};

const ENTITY_LABELS = {
  user: "Utente",
  cantiere: "Cantiere",
  rapportino: "Rapportino",
  materiale: "Materiale",
  bolla: "Bolla",
  ferie: "Ferie/Permesso",
  backup: "Backup",
};

const eur = (n) => new Intl.NumberFormat("it-IT", {style:"currency",currency:"EUR"}).format(n || 0);
const kb = (n) => `${(n / 1024).toFixed(1)} KB`;

export default function AdminTools() {
  const [tab, setTab] = useState("backup");
  const tabs = [
    { id: "backup", label: "Backup MongoDB", icon: Database },
    { id: "trash",  label: "Cestino", icon: Trash2 },
    { id: "audit",  label: "Log attività (GDPR)", icon: FileClock },
  ];
  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Strumenti amministratore</div>
        <h1 className="text-3xl font-display font-black mt-1">Backup · Cestino · Log</h1>
        <p className="text-sm text-slate-600 mt-1">
          Backup automatici del database, ripristino di elementi eliminati e registro completo delle attività.
        </p>
      </div>

      <div className="border-b border-slate-200 flex gap-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`tab-${id}`}
            className={`px-4 py-2 -mb-px flex items-center gap-2 border-b-2 text-sm font-semibold ${
              tab === id
                ? "border-blue-700 text-blue-800"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "backup" && <BackupPanel />}
      {tab === "trash"  && <TrashPanel />}
      {tab === "audit"  && <AuditPanel />}
    </div>
  );
}

// -------------------- BACKUP --------------------
function BackupPanel() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["backups"],
    queryFn: () => api.get("/admin/backups").then((r) => r.data),
    refetchInterval: 30000,
  });
  const create = useMutation({
    mutationFn: () => api.post("/admin/backups/create"),
    onSuccess: () => {
      toast.success("Backup creato");
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore creazione backup"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/backups/${id}`),
    onSuccess: () => {
      toast.success("Backup eliminato");
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-900">
        <strong>Backup automatico:</strong> ogni notte alle <b>03:00 UTC</b> viene creato uno snapshot completo del database MongoDB.
        Retention 14 giorni. Il file .tar.gz contiene un JSON per ogni collezione + metadati.
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {items.length} backup memorizzati · storage <code className="px-1 py-0.5 bg-slate-100 rounded">{"/data/backups"}</code>
        </div>
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending}
          data-testid="backup-create-now"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold rounded-sm disabled:opacity-60"
        >
          <Plus className="w-4 h-4" />
          {create.isPending ? "Creazione in corso..." : "Crea backup ora"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Innescato da</th>
              <th className="text-right p-3">Collezioni</th>
              <th className="text-right p-3">Documenti</th>
              <th className="text-right p-3">Dimensione</th>
              <th className="text-left p-3">Nome file</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Caricamento...</td></tr>}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Nessun backup ancora. Il primo scatta stanotte alle 03:00 UTC, oppure premi «Crea backup ora».</td></tr>
            )}
            {items.map((b) => (
              <tr key={b.backup_id} className="border-t border-slate-100" data-testid={`backup-${b.backup_id}`}>
                <td className="p-3 font-mono text-xs">{b.created_at?.replace("T", " ").slice(0, 19)}</td>
                <td className="p-3 text-xs">{b.triggered_by}</td>
                <td className="p-3 text-right font-mono">{b.collections}</td>
                <td className="p-3 text-right font-mono">{b.total_docs}</td>
                <td className="p-3 text-right font-mono">{kb(b.size_bytes)}</td>
                <td className="p-3 font-mono text-xs">{b.filename}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <a
                      href={`${backendUrl}/api/admin/backups/${b.backup_id}/download`}
                      className={`p-1.5 rounded-sm border ${b.file_exists ? "border-blue-300 text-blue-700 hover:bg-blue-50" : "border-slate-300 text-slate-300 pointer-events-none"}`}
                      title={b.file_exists ? "Scarica" : "File mancante sul disco"}
                      data-testid={`backup-download-${b.backup_id}`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => {
                        if (window.confirm(`Eliminare backup ${b.filename}?`))
                          del.mutate(b.backup_id);
                      }}
                      className="p-1.5 rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50"
                      title="Elimina"
                      data-testid={`backup-delete-${b.backup_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- TRASH --------------------
function TrashPanel() {
  const qc = useQueryClient();
  const { data = {}, isLoading } = useQuery({
    queryKey: ["trash"],
    queryFn: () => api.get("/admin/trash").then((r) => r.data),
  });
  const restore = useMutation({
    mutationFn: ({ entity, id }) => api.post(`/admin/trash/${entity}/${id}/restore`, { reason: "" }),
    onSuccess: () => {
      toast.success("Ripristinato");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const purge = useMutation({
    mutationFn: ({ entity, id }) => api.delete(`/admin/trash/${entity}/${id}/purge`),
    onSuccess: () => {
      toast.success("Eliminato definitivamente");
      qc.invalidateQueries({ queryKey: ["trash"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const sections = [
    { entity: "cantiere",   id_field: "cantiere_id",   title: (x) => `${x.nome} — ${x.cliente}` },
    { entity: "rapportino", id_field: "rapportino_id", title: (x) => `${x.data} — ${x.cantiere_nome} (${x.responsabile})` },
    { entity: "bolla",      id_field: "bolla_id",      title: (x) => `${x.numero_bolla} · ${x.fornitore} · ${x.materiale}` },
    { entity: "materiale",  id_field: "materiale_id",  title: (x) => `${x.nome} (${x.categoria})` },
    { entity: "user",       id_field: "user_id",       title: (x) => `${x.name} · ${x.email}` },
  ];

  if (isLoading) return <div className="p-8 text-center text-slate-400">Caricamento cestino...</div>;

  const empty = Object.values(data).every((v) => (v || []).length === 0);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900">
        <strong>Eliminazione logica:</strong> gli oggetti eliminati vengono spostati qui e possono essere ripristinati.
        L'eliminazione <strong>definitiva</strong> (icona rossa) rimuove il documento in modo permanente e non è annullabile.
      </div>

      {empty && (
        <div className="p-12 text-center text-slate-400 bg-white border border-slate-200 rounded-md">
          Il cestino è vuoto.
        </div>
      )}

      {sections.map(({ entity, id_field, title }) => {
        const rows = data[entity] || [];
        if (!rows.length) return null;
        return (
          <div key={entity} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="p-3 border-b border-slate-200 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Trash2 className="w-4 h-4 text-slate-500" />
              {ENTITY_LABELS[entity]} ({rows.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left p-3">Descrizione</th>
                  <th className="text-left p-3">Eliminato il</th>
                  <th className="text-left p-3">Da</th>
                  <th className="text-right p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r[id_field]} className="border-t border-slate-100" data-testid={`trash-${entity}-${r[id_field]}`}>
                    <td className="p-3">{title(r)}</td>
                    <td className="p-3 font-mono text-xs">{(r.deleted_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="p-3 text-xs">{r.deleted_by || "—"}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => restore.mutate({ entity, id: r[id_field] })}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-blue-300 text-blue-700 hover:bg-blue-50 text-xs"
                          title="Ripristina"
                          data-testid={`restore-${r[id_field]}`}
                        >
                          <RotateCcw className="w-3 h-3" /> Ripristina
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Eliminare DEFINITIVAMENTE ${title(r)}?\n\nQuesta azione è IRREVERSIBILE.`))
                              purge.mutate({ entity, id: r[id_field] });
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs"
                          title="Elimina definitivamente"
                          data-testid={`purge-${r[id_field]}`}
                        >
                          <ShieldAlert className="w-3 h-3" /> Elimina def.
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// -------------------- AUDIT LOG --------------------
function AuditPanel() {
  const [filters, setFilters] = useState({ entity_type: "", action: "", user_id: "", da: "", a: "" });
  const { data, isLoading } = useQuery({
    queryKey: ["audit-log", filters],
    queryFn: () => api.get("/admin/audit-log", { params: filters }).then((r) => r.data),
  });

  const clear = () => setFilters({ entity_type: "", action: "", user_id: "", da: "", a: "" });

  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm text-sm text-slate-700">
        <strong>Registro attività GDPR:</strong> ogni operazione di creazione/modifica/eliminazione/approvazione
        eseguita dagli amministratori viene tracciata con utente, timestamp e diff dei campi.
      </div>

      {/* filters */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Entità</label>
          <select
            value={filters.entity_type}
            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
            data-testid="audit-filter-entity"
            className="px-2 py-1 border border-slate-300 rounded-sm text-sm"
          >
            <option value="">Tutte</option>
            {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Azione</label>
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="px-2 py-1 border border-slate-300 rounded-sm text-sm"
          >
            <option value="">Tutte</option>
            {Object.keys(ACTION_COLORS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Dal</label>
          <input type="date" value={filters.da} onChange={(e) => setFilters({ ...filters, da: e.target.value })}
                 className="px-2 py-1 border border-slate-300 rounded-sm text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Al</label>
          <input type="date" value={filters.a} onChange={(e) => setFilters({ ...filters, a: e.target.value })}
                 className="px-2 py-1 border border-slate-300 rounded-sm text-sm" />
        </div>
        <button onClick={clear} className="px-3 py-1 border border-slate-300 rounded-sm text-sm hover:bg-slate-50 inline-flex items-center gap-1">
          <X className="w-3 h-3" /> Reset
        </button>
        <div className="ml-auto text-sm text-slate-600 self-center">
          {isLoading ? "Caricamento..." : `${data?.total || 0} righe`}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr>
              <th className="text-left p-3">Quando</th>
              <th className="text-left p-3">Utente</th>
              <th className="text-left p-3">Azione</th>
              <th className="text-left p-3">Entità</th>
              <th className="text-left p-3">ID</th>
              <th className="text-left p-3">Modifiche</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((it) => (
              <tr key={it.audit_id} className="border-t border-slate-100 align-top">
                <td className="p-3 font-mono text-xs whitespace-nowrap">{(it.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-3 text-xs">
                  <div className="font-semibold">{it.user_email || "—"}</div>
                  <div className="text-slate-500">{it.user_role}</div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm border ${ACTION_COLORS[it.action] || "bg-slate-100 text-slate-800 border-slate-200"}`}>
                    {it.action}
                  </span>
                </td>
                <td className="p-3 text-xs">{ENTITY_LABELS[it.entity_type] || it.entity_type}</td>
                <td className="p-3 font-mono text-xs">{it.entity_id}</td>
                <td className="p-3 text-xs max-w-md">
                  {it.changes && Object.keys(it.changes).length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-blue-700 font-semibold">
                        {Object.keys(it.changes).length === 1 && it.changes.__created__
                          ? "creato"
                          : Object.keys(it.changes).length === 1 && it.changes.__deleted__
                          ? "eliminato"
                          : `${Object.keys(it.changes).length} campi cambiati`}
                      </summary>
                      <pre className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-[10px] overflow-x-auto max-h-64">
                        {JSON.stringify(it.changes, null, 2)}
                      </pre>
                    </details>
                  )}
                  {it.reason && <div className="mt-1 text-slate-500 italic">Motivo: {it.reason}</div>}
                </td>
              </tr>
            ))}
            {!isLoading && (data?.items || []).length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Nessuna attività registrata con questi filtri.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
